'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  FilePlus2,
  ClipboardList,
  CheckCircle2,
  Banknote,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Settings,
  LogOut,
  X,
} from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    id: 'section-main',
    title: 'Main',
    items: [
      {
        id: 'nav-dashboard',
        label: 'Approval Dashboard',
        href: '/',
        icon: <LayoutDashboard size={18} />,
      },
    ],
  },
  {
    id: 'section-fund',
    title: 'Fund Requests',
    items: [
      {
        id: 'nav-new-fund',
        label: 'New Fund Request',
        href: '/project-fund-request-form',
        icon: <FilePlus2 size={18} />,
      },
      {
        id: 'nav-submitted',
        label: 'Submitted Requests',
        href: '/submitted-requests',
        icon: <ClipboardList size={18} />,
        badge: 7,
      },
      {
        id: 'nav-approved',
        label: 'Approved Requests',
        href: '/approved-requests',
        icon: <CheckCircle2 size={18} />,
        badge: 3,
      },
      {
        id: 'nav-released',
        label: 'Released Requests',
        href: '/released-requests',
        icon: <Banknote size={18} />,
      },
    ],
  },
  {
    id: 'section-leave',
    title: 'Leave',
    items: [
      {
        id: 'nav-leave',
        label: 'Leave Management',
        href: '/leave',
        icon: <CalendarCheck size={18} />,
      },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}

export default function Sidebar({ collapsed, onToggle, onMobileClose }: SidebarProps) {
  const pathname = usePathname();

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border sidebar-transition ${
        collapsed ? 'w-16' : 'w-60'
      } min-h-screen shrink-0`}
    >
      {/* Logo */}
      <div
        className={`flex items-center border-b border-border h-16 px-3 ${
          collapsed ? 'justify-center' : 'gap-2'
        }`}
      >
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-sm text-foreground tracking-tight truncate flex-1">
            PaymentApproval
          </span>
        )}
        {/* Mobile close button */}
        {!collapsed && onMobileClose && (
          <button
            onClick={onMobileClose}
            className="lg:hidden p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ml-auto"
            aria-label="Close navigation"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {navSections.map((section) => (
          <div key={section.id}>
            {!collapsed && (
              <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-2 mb-1">
                {section.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = pathname === item.href;
                return (
                  <li key={item.id}>
                    <Link
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      onClick={onMobileClose}
                      className={`group flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-500 transition-all duration-150 relative ${
                        isActive
                          ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <span className="shrink-0">{item.icon}</span>
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                      {!collapsed && item.badge != null && (
                        <span className="ml-auto shrink-0 bg-primary text-primary-foreground text-[10px] font-700 rounded-full px-1.5 py-0.5 tabular-nums">
                          {item.badge}
                        </span>
                      )}
                      {collapsed && item.badge != null && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2 space-y-0.5">
        <Link
          href="#"
          title={collapsed ? 'Settings' : undefined}
          onClick={onMobileClose}
          className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-500 text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150"
        >
          <Settings size={18} className="shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>
        <button
          title={collapsed ? 'Sign Out' : undefined}
          className="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-500 text-muted-foreground hover:bg-danger/10 hover:text-danger transition-all duration-150"
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Collapse toggle — desktop only */}
      <button
        onClick={onToggle}
        className="hidden lg:flex absolute -right-3 top-20 z-10 w-6 h-6 rounded-full bg-card border border-border shadow-card items-center justify-center text-muted-foreground hover:text-foreground hover:shadow-card-hover transition-all duration-150"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}